/**
 * Visitor identity module
 *
 * Maintains a persistent first-party visitor id (cookie + localStorage mirror)
 * so sessions can be stitched to one person across visits, and exposes
 * identify() so customers can link a visitor to their signed-up user.
 * The cookie lives on the customer's domain, so it survives OAuth redirects.
 */

import { sessionState } from './state';

const VISITOR_COOKIE = 'khabib_visitor_id';
const VISITOR_STORAGE_KEY = '__khabib_visitor_id';
const IDENTIFY_STORAGE_KEY = '__khabib_identify';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 2; // 2 years

export interface IdentifyTraits {
  email?: string;
  name?: string;
  [key: string]: unknown;
}

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string): void {
  try {
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; max-age=' + COOKIE_MAX_AGE +
      '; path=/; SameSite=Lax';
  } catch {
    // cookies unavailable (e.g. sandboxed iframe) — localStorage mirror still works
  }
}

function safeStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage unavailable
  }
}

/**
 * Read the persistent visitor id, creating it on first visit.
 * Cookie is the source of truth; localStorage is a recovery mirror.
 */
export function getOrCreateVisitorId(): string {
  let visitorId = readCookie(VISITOR_COOKIE) || safeStorageGet(VISITOR_STORAGE_KEY);

  if (!visitorId) {
    try {
      visitorId = crypto.randomUUID();
    } catch {
      visitorId = 'v-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }
  }

  // Refresh both stores every load (extends cookie expiry, repairs a cleared store)
  writeCookie(VISITOR_COOKIE, visitorId);
  safeStorageSet(VISITOR_STORAGE_KEY, visitorId);

  return visitorId;
}

/**
 * Derive the identify endpoint from the ingest backend URL.
 */
function identifyUrl(): string {
  return sessionState.backendUrl.replace(/\/[^/]+$/, '/identify');
}

function sendIdentifyPayload(payload: Record<string, unknown>): void {
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      // text/plain keeps the beacon a "simple" CORS request: beacons are
      // always credentialed, and a JSON content type would force a preflight
      // that fails against our wildcard Access-Control-Allow-Origin.
      const blob = new Blob([body], { type: 'text/plain' });
      if (navigator.sendBeacon(identifyUrl(), blob)) return;
    }
  } catch {
    // fall through to fetch
  }
  fetch(identifyUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    mode: 'cors',
    credentials: 'omit',
  }).catch(function () {});
}

/**
 * Public identify() — customers call this after signup/login
 * (works for any auth method, including OAuth):
 *   window.khabib.identify('user_123', { email: 'sam@acme.com', name: 'Sam' });
 */
export function identify(externalUserId?: string | null, traits?: IdentifyTraits): void {
  if (!sessionState.projectId || !sessionState.visitorId) return;

  const payload = {
    projectId: sessionState.projectId,
    visitorId: sessionState.visitorId,
    externalUserId: externalUserId || null,
    email: traits?.email || null,
    name: traits?.name || null,
    traits: traits || {},
  };

  // Cache so the next page load re-sends in case the beacon is dropped
  safeStorageSet(IDENTIFY_STORAGE_KEY, JSON.stringify(payload));

  sendIdentifyPayload(payload);
}

/**
 * Re-send the last cached identify on a new page load (idempotent upsert
 * server-side) so a dropped beacon doesn't lose the identity.
 */
export function resendStoredIdentify(): void {
  const stored = safeStorageGet(IDENTIFY_STORAGE_KEY);
  if (!stored) return;
  try {
    const payload = JSON.parse(stored);
    if (payload && payload.projectId === sessionState.projectId) {
      payload.visitorId = sessionState.visitorId;
      sendIdentifyPayload(payload);
    }
  } catch {
    // corrupt cache — ignore
  }
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
let lastCapturedEmail: string | null = null;

/**
 * Passive email capture fallback for customers who haven't added identify().
 * Catches in-page forms (email/password, newsletter, etc.);
 * OAuth signups (Google/GitHub) have no in-page email field and are only
 * covered by identify().
 */
export function setupPassiveCapture(): void {
  document.addEventListener(
    'submit',
    function (e) {
      try {
        const form = e.target as HTMLFormElement;
        if (!form || !form.querySelectorAll) return;

        const inputs = form.querySelectorAll<HTMLInputElement>('input[type="email"], input[name*="email" i], input[id*="email" i], input[type="text"]');
        for (let i = 0; i < inputs.length; i++) {
          const value = (inputs[i].value || '').trim();
          if (value && EMAIL_REGEX.test(value)) {
            if (value !== lastCapturedEmail) {
              lastCapturedEmail = value;
              identify(null, { email: value });
            }
            return;
          }
        }
      } catch {
        // never break the host page
      }
    },
    true
  );
}
